import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { Card, EmptyState, ErrorState, SkeletonRows, TokenLogo } from "@/components/common";
import { fmtCompact, fmtUsd } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

export default function StockTokensPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  usePageTitle("Stock Tokens — Tokenized Equities", "Live prices, holders and activity for Robinhood tokenized stocks (AAPL, NVDA, TSLA and more) on Robinhood Chain.");

  useEffect(() => {
    api.get("/stock-tokens").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);

  const items = (data?.items || []).filter(
    (t) => !q || (t.name || "").toLowerCase().includes(q.toLowerCase()) || (t.symbol || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6 pt-8">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Landmark size={22} className="text-primary" /> Stock Tokens
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Robinhood tokenized equities live on Robinhood Chain.</p>
        </div>
        <input data-testid="stocks-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by company or ticker…"
          className="ml-auto w-full sm:w-72 bg-card border rounded-lg px-3 py-2 text-xs outline-none focus:border-primary" />
      </div>

      <Card className="p-4 border-yellow-500/30 bg-yellow-500/5">
        <p className="text-xs leading-relaxed text-muted-foreground" data-testid="stocks-page-disclaimer">
          <span className="font-semibold text-foreground">Important:</span> blockchain stock tokens may not be the same as owning a
          traditional share through a normal broker. Token values include Robinhood's corporate-action multiplier (dividends, splits),
          so one token may not always equal one share. GMScan is independent and not operated by Robinhood.
        </p>
      </Card>

      {err ? <ErrorState /> : data === null ? <SkeletonRows n={8} h="h-16" /> : items.length === 0 ? (
        <Card><EmptyState title="No stock tokens found" subtitle={q ? "Try a different search." : "Tokenized equities will appear here as they are detected on Robinhood Chain."} /></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Link key={t.address_hash} to={`/token/${t.address_hash}`}>
              <Card className="p-4 h-full transition-transform duration-300 ease-out hover:-translate-y-1 hover:border-primary/40" data-testid="stock-card">
                <div className="flex items-center gap-3">
                  <TokenLogo url={t.icon_url} symbol={t.symbol} size={36} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{t.company_name}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">{t.symbol} · Tokenized equity</p>
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Reference Price</p>
                    <p className="text-lg font-semibold font-mono">{t.exchange_rate ? fmtUsd(t.exchange_rate) : "Price unavailable"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Holders</p>
                    <p className="text-sm font-mono">{fmtCompact(t.holders_count)}</p>
                  </div>
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground">Market value {t.circulating_market_cap ? fmtUsd(t.circulating_market_cap, 0) : "—"} · Vol {t.volume_24h ? fmtUsd(t.volume_24h, 0) : "—"}</p>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {data?.note && <p className="text-[10px] text-muted-foreground">{data.note}</p>}
    </div>
  );
}
